import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FaShoppingCart, 
  FaHeart, 
  FaRegHeart, 
  FaChevronLeft, 
  FaChevronRight, 
  FaCheck,
  FaMinus, 
  FaPlus,
  FaStar,
  FaBoxOpen,
  FaShieldAlt,
  FaTags
} from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import api, { favoritesAPI } from '../api/axios';
import { useAuth } from '../context/AuthContext';

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cart } = useCart();
  const { user } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [ratingLoading, setRatingLoading] = useState(false);

  useEffect(() => {
    const fetchProductAndFavorites = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/products/${id}`);
        const productData = res.data.data;
        setProduct(productData);
        if (productData.colors?.length) setSelectedColor(productData.colors[0]);
        if (productData.sizes?.length) setSelectedSize(productData.sizes[0]);
        await checkIfFavorite();
      } catch (error) {
        toast.error('Error al cargar el producto');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchProductAndFavorites();
  }, [id, navigate]);

  const checkIfFavorite = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await favoritesAPI.getFavorites();
      const favorites = response.data;
      const isFav = favorites.some(fav => fav._id === id);
      setIsFavorite(isFav);
    } catch (error) {
      if (error.response?.status !== 401) console.error('Error al verificar favoritos:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) return navigate('/login', { state: { from: `/product/${id}` } });
    if (!product) return;
    setFavoriteLoading(true);
    try {
      const response = await favoritesAPI.toggleFavorite(product._id);
      setIsFavorite(response.data.isFavorite);
      toast.success(response.data.isFavorite ? 'Agregado a favoritos' : 'Removido de favoritos');
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const submitRating = async (value) => {
    if (!user) return navigate('/login', { state: { from: `/product/${id}` } });
    try {
      setRatingLoading(true);
      const { data } = await api.post(`/products/${id}/rating`, { value });
      setProduct(prev => ({ ...prev, rating: data.rating, reviewCount: data.reviewCount }));
      toast.success('Gracias por calificar este producto');
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudo guardar tu calificación');
    } finally {
      setRatingLoading(false);
    }
  };

  const handleAddToCart = useCallback(async () => {
    if (!product || quantity < 1) return;
    setIsAddingToCart(true);
    try {
      addToCart({
        ...product,
        selectedColor,
        selectedSize
      }, quantity);
      toast.success('Producto agregado al carrito');
    } finally {
      setIsAddingToCart(false);
    }
  }, [product, quantity, selectedColor, selectedSize, addToCart]);

  const handleQuantityChange = useCallback((value) => {
    const newQuantity = parseInt(value);
    if (!isNaN(newQuantity) && newQuantity >= 1 && newQuantity <= (product?.stock || 10)) {
      setQuantity(newQuantity);
    }
  }, [product?.stock]);

  const incrementQuantity = useCallback(() => {
    if (quantity < (product?.stock || 10)) setQuantity(quantity + 1);
  }, [quantity, product?.stock]);

  const decrementQuantity = useCallback(() => {
    if (quantity > 1) setQuantity(quantity - 1);
  }, [quantity]);

  const nextImage = useCallback(() => {
    setSelectedImage((prev) => (prev + 1) % (product?.images?.length || 1));
  }, [product?.images?.length]);

  const prevImage = useCallback(() => {
    setSelectedImage((prev) => (prev - 1 + (product?.images?.length || 1)) % (product?.images?.length || 1));
  }, [product?.images?.length]);

  const getImageUrl = (imageName) => {
    if (!imageName) return '/placeholder.jpg';
    if (imageName.startsWith('http')) return imageName;
    if (imageName.includes('uploads/')) return imageName.startsWith('/') ? imageName : `/${imageName}`;
    return `/uploads/products/${imageName}`;
  };

  // ✅ GALERÍA MEJORADA - SIN DESBORDAMIENTO
  const ImageGallery = ({ images, selectedImage, onSelectImage, onPrev, onNext }) => (
    <div className="w-full lg:w-1/2 max-w-full">
      {/* IMAGEN PRINCIPAL CON ANCHO MÁXIMO CONTROLADO */}
      <div className="relative bg-gray-100 rounded-lg aspect-square flex items-center justify-center p-4 mb-4 max-w-full overflow-hidden">
        <img
          src={images[selectedImage]}
          alt={product.name}
          className="max-h-full max-w-full object-contain"
        />
        
        {/* SOLO BOTONES DE NAVEGACIÓN CUANDO HAY MÚLTIPLES IMÁGENES */}
        {images.length > 1 && (
          <>
            <button 
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 z-10"
            >
              <FaChevronLeft className="text-sm" />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 z-10"
            >
              <FaChevronRight className="text-sm" />
            </button>
            
            {/* CONTADOR SIMPLE EN ESQUINA INFERIOR DERECHA */}
            <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs z-10">
              {selectedImage + 1}/{images.length}
            </div>
          </>
        )}
      </div>

      {/* ✅ MINIATURAS CON SCROLL MEJORADO - SIN DESBORDAMIENTO */}
      {images.length > 1 && (
        <div className="overflow-x-auto max-w-full px-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="flex gap-2 pb-2 min-w-max">
            {images.map((img, index) => (
              <button
                key={index}
                onClick={() => onSelectImage(index)}
                className={`flex-shrink-0 w-12 h-12 rounded border transition-all ${
                  selectedImage === index 
                    ? 'border-blue-500 ring-1 ring-blue-300' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <img 
                  src={img} 
                  alt={`Miniatura ${index + 1}`}
                  className="w-full h-full object-cover rounded"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Loading State
  const LoadingState = () => (
    <div className="min-h-screen pt-32 pb-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="animate-pulse bg-white rounded-lg p-6">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/2 h-80 bg-gray-200 rounded-lg"></div>
            <div className="lg:w-1/2 space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Error State
  const ErrorState = () => (
    <div className="min-h-screen pt-32 pb-20 px-4 flex items-center justify-center">
      <div className="text-center max-w-md p-6 bg-white rounded-lg">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Producto no encontrado</h2>
        <button 
          onClick={() => navigate('/')}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded font-medium hover:bg-blue-700"
        >
          Volver a la tienda
        </button>
      </div>
    </div>
  );

  if (loading) return <LoadingState />;
  if (!product) return <ErrorState />;

  // Preparar imágenes
  const images = product.images && product.images.length > 0 
    ? product.images.map(img => getImageUrl(img)) 
    : product.image 
      ? [getImageUrl(product.image)] 
      : ['/placeholder.jpg'];

  const isInCart = cart.some(item => item.product._id === product._id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5fbfa] via-white to-[#f8f0fc] pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* BREADCRUMB SIMPLE */}
        <button 
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-[#0C4B45] hover:text-[#662D8F] font-medium text-sm mb-6 transition-colors"
        >
          ← Volver
        </button>

        {/* CONTENEDOR PRINCIPAL CON ANCHO CONTROLADO */}
        <div className="bg-white/95 rounded-3xl shadow-xl border border-white max-w-full overflow-hidden">
          <div className="flex flex-col lg:flex-row gap-8 p-5 md:p-8">
            {/* GALERÍA */}
            <ImageGallery 
              images={images}
              selectedImage={selectedImage}
              onSelectImage={setSelectedImage}
              onPrev={prevImage}
              onNext={nextImage}
            />

            {/* INFORMACIÓN DEL PRODUCTO */}
            <div className="lg:w-1/2 max-w-full">
              {/* HEADER COMPACTO */}
              <div className="flex justify-between items-start mb-4 max-w-full">
                <div className="flex-1 max-w-full">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#662D8F] mb-2"><FaTags /> {product.category || 'Producto'}</div>
                  <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0C4B45] mb-2">
                    {product.name}
                  </h1>
                  
                  {/* PRECIO */}
                  <div className="flex items-baseline gap-2 mb-4 flex-wrap">
                    <span className="text-3xl font-extrabold text-[#662D8F]">
                      ${product.price?.toLocaleString()}
                    </span>
                    {product.originalPrice && product.price < product.originalPrice && (
                      <span className="text-gray-400 line-through text-sm">
                        ${product.originalPrice.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* BOTÓN FAVORITO */}
                <button 
                  onClick={toggleFavorite}
                  disabled={favoriteLoading}
                  className={`p-2 rounded-full flex-shrink-0 ${
                    isFavorite ? 'text-red-500' : 'text-gray-400'
                  }`}
                >
                  {isFavorite ? <FaHeart /> : <FaRegHeart />}
                </button>
              </div>

              {/* DESCRIPCIÓN */}
              <p className="text-gray-600 text-base mb-6 leading-relaxed max-w-full">
                {product.description}
              </p>

              <section className="mb-6 rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50 to-white p-5 shadow-sm">
                <p className="font-bold text-[#0C4B45]">Calificación de clientes</p>
                <div className="mt-2 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(star => <FaStar key={star} className={star <= Math.round(product.rating || 0) ? 'text-yellow-400' : 'text-gray-300'} />)}
                  <span className="ml-2 text-sm text-gray-600">{product.rating || 0}/5 ({product.reviewCount || 0} calificaciones)</span>
                </div>
                <p className="mt-3 text-sm text-gray-700">¿Qué te pareció? Selecciona de una a cinco estrellas.</p>
                <div className="mt-2 flex gap-2">
                  {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" disabled={ratingLoading} onClick={() => submitRating(star)} aria-label={`Calificar con ${star} estrellas`} className="text-2xl transition-transform hover:scale-110 disabled:opacity-50"><FaStar className="text-yellow-400" /></button>)}
                </div>
              </section>

              {/* OPCIONES SI EXISTEN */}
              {product.colors?.length > 0 && (
                <div className="mb-4 max-w-full">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Color:</h3>
                  <div className="flex flex-wrap gap-1">
                    {product.colors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        className={`w-8 h-8 rounded-full border flex-shrink-0 ${
                          selectedColor === color ? 'border-blue-500' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {product.sizes?.length > 0 && (
                <div className="mb-4 max-w-full">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Talla:</h3>
                  <div className="flex flex-wrap gap-1">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-3 py-1 border rounded text-sm flex-shrink-0 ${
                          selectedSize === size 
                            ? 'bg-blue-600 text-white border-blue-600' 
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STOCK SIMPLE */}
              <div className="mb-4 max-w-full">
                <div className="rounded-xl bg-[#f5fbfa] p-4 flex items-center justify-between text-sm mb-1">
                  <span className="text-[#0C4B45] flex items-center gap-2"><FaBoxOpen /> Disponible</span>
                  <span className="text-green-700 font-bold">{product.stock} unidades</span>
                </div>
              </div>

              {/* CANTIDAD Y CARRITO */}
              <div className="mb-6 max-w-full">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center border border-gray-300 rounded flex-shrink-0">
                    <button 
                      onClick={decrementQuantity}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600"
                    >
                      <FaMinus className="text-xs" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={product.stock}
                      value={quantity}
                      onChange={(e) => handleQuantityChange(e.target.value)}
                      className="w-12 text-center border-x focus:outline-none"
                    />
                    <button 
                      onClick={incrementQuantity}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600"
                    >
                      <FaPlus className="text-xs" />
                    </button>
                  </div>
                  
                  <button
                    onClick={handleAddToCart}
                    disabled={isInCart || isAddingToCart}
                    className={`flex-1 py-2 rounded font-medium min-w-0 ${
                      isInCart 
                        ? 'bg-gray-100 text-gray-600' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isAddingToCart ? (
                      'Procesando...'
                    ) : isInCart ? (
                      'En el carrito'
                    ) : (
                      'Agregar al carrito'
                    )}
                  </button>
                </div>
              </div>

              {/* INFORMACIÓN ADICIONAL COMPACTA */}
              <div className="border-t pt-5 max-w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600">
                  <div className="rounded-xl bg-gray-50 p-3"><span className="font-semibold text-[#0C4B45]">Categoría</span><p>{product.category || 'No especificada'}</p></div>
                  <div className="rounded-xl bg-gray-50 p-3"><span className="font-semibold text-[#0C4B45]">Marca</span><p>{product.brand || 'No especificada'}</p></div>
                  {product.warranty && (
                    <div className="rounded-xl bg-gray-50 p-3 sm:col-span-2 flex items-center gap-2">
                      <FaShieldAlt className="text-[#662D8F]" /><span><span className="font-semibold text-[#0C4B45]">Garantía:</span> {product.warranty} {product.warranty === '1' ? 'mes' : 'meses'}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
